import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";

import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import { SegmentationService } from "../../services/segmentation/SegmentationService";
import { closestSegmentationsToTime, drawSegmentations } from "../../helpers/SegmentationRenderer";

// The segmentations that should currently be drawn for a service, after applying
// the user's confidence option (Recommended / Individual / Overall). Shared by the
// canvas overlay and the video controls' progress-bar markers so both stay in sync.
export function useFilteredSegmentations(
  videoSegmentation?: SegmentationService
): Segmentation[] {
  return useMemo(() => {
    if (videoSegmentation?.isLoading || !videoSegmentation?.segmentationSuccessful) return [];

    if (
      videoSegmentation?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended ||
      videoSegmentation?.highlightDetectionConfidenceOption === ConfidenceOptions.Individual
    ) {
      let defaultSegmentations: Segmentation[] = [];

      const confidenceLevels =
        videoSegmentation?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended
          ? videoSegmentation?.vaData.recommendedConfidenceLevels
          : videoSegmentation?.detectionIndividualConfidenceLevels;

      for (const groupCode in videoSegmentation?.detectedGroupCodes) {
        defaultSegmentations = [
          ...defaultSegmentations,
          ...videoSegmentation?.vaData.segmentationDetails?.segmentations.filter((x) => {
            return (
              x.groupCode === videoSegmentation?.detectedGroupCodes[groupCode].code &&
              x.accuracy === confidenceLevels[videoSegmentation?.detectedGroupCodes[groupCode].code]
            );
          }),
        ];
      }

      return defaultSegmentations;
    }

    return videoSegmentation?.vaData.segmentationDetails?.segmentations.filter(
      (x) => x.accuracy === videoSegmentation?.highlightDetectionOverallConfidence
    );
  }, [
    videoSegmentation?.detectedGroupCodes,
    videoSegmentation?.detectionIndividualConfidenceLevels,
    videoSegmentation?.highlightDetectionConfidenceOption,
    videoSegmentation?.highlightDetectionOverallConfidence,
    videoSegmentation?.isLoading,
    videoSegmentation?.segmentationSuccessful,
    videoSegmentation?.vaData.recommendedConfidenceLevels,
    videoSegmentation?.vaData.segmentationDetails?.segmentations,
  ]);
}

interface SegmentationCanvasOverlayProps {
  videoSegmentation?: SegmentationService;
  // Reuse a list computed by the parent (e.g. VideoAnalysisPlayer also feeds it to
  // the progress bar). When omitted the overlay computes its own.
  filteredSegmentations?: Segmentation[];
}

// Absolutely-positioned canvas overlaid on the shared video player (#my-video).
// On every animation frame it redraws the segmentation polygons closest to the
// current playback time. Extracted from VideoAnalysisPlayer so the same overlay can
// be dropped onto the Inspection Insights video without nesting a second VideoPlayer.
export default observer(function SegmentationCanvasOverlay(
  props: SegmentationCanvasOverlayProps
) {
  const {
    isFullscreen,
    videoProgress,
    showArea,
    showBox,
    showContour,
    showLabels,
    playerWidth: videoPlayerWidth,
    playerHeight: videoPlayerHeight,
    setWidth: setVideoPlayerWidth,
    setHeight: setVideoPlayerHeight,
  } = videoPlayerService;

  const { videoSegmentation } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestId = useRef<number>();

  const ownFilteredSegmentations = useFilteredSegmentations(videoSegmentation);
  const filteredSegmentations = props.filteredSegmentations ?? ownFilteredSegmentations;

  const renderSegmentationsBasedOnGroupCode = useCallback(
    (segmentations: Segmentation[], context: CanvasRenderingContext2D) => {
      const imageSize = videoSegmentation?.vaData.segmentationDetails.imageSize;
      if (!imageSize?.width || !imageSize?.height) return;
      const color = videoSegmentation?.getGroupCodeColors()[segmentations[0].groupCode.toLowerCase()];

      drawSegmentations(
        context,
        segmentations,
        color,
        videoPlayerWidth / imageSize.width,
        videoPlayerHeight / imageSize.height,
        {
          showArea,
          showContour,
          showBox,
          lineWidth: videoPlayerService.isFullscreen ? 4 : 2,
          label: showLabels ? segmentations[0].groupCode : undefined,
        }
      );
    },
    [showArea, showBox, showContour, showLabels, videoSegmentation, videoPlayerHeight, videoPlayerWidth]
  );

  const detectedGroupCodes = JSON.stringify(videoSegmentation?.detectedGroupCodes);

  const renderClosestPredictionBasedOnVideoTime = useCallback(
    (context: CanvasRenderingContext2D, currentVideoTime: number) => {
      context!.clearRect(0, 0, videoPlayerWidth, videoPlayerHeight);

      const CLOSEST_PREDICTION_THRESHOLD = 0.15;
      // Single O(n) pass (was O(n^2): the closest-timestamp reduce ran once per element
      // inside the filter, re-scanning all segmentations on every frame while playing).
      const closestSegmentations = closestSegmentationsToTime(
        filteredSegmentations ?? [],
        currentVideoTime,
        CLOSEST_PREDICTION_THRESHOLD
      );

      if (!closestSegmentations.length) {
        return;
      }

      videoSegmentation?.detectedGroupCodes?.forEach((groupCode) => {
        if (groupCode.isVisible && groupCode.isDetected) {
          const groupCodeClosestSegmentations = closestSegmentations?.filter(
            (s) => s.groupCode === groupCode.code
          );

          if (groupCodeClosestSegmentations?.length) {
            renderSegmentationsBasedOnGroupCode(groupCodeClosestSegmentations, context!);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      renderSegmentationsBasedOnGroupCode,
      videoSegmentation?.getGroupCodeColors,
      detectedGroupCodes,
      videoPlayerHeight,
      videoPlayerWidth,
      filteredSegmentations,
    ]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const videoElement = document.querySelector("#my-video > video") as any;

    if (!context || videoSegmentation?.isLoading || !videoElement) return;

    const renderFramePredictions = () => {
      const computedWidth =
        (videoElement?.clientHeight * videoElement.videoWidth) / videoElement.videoHeight;

      setVideoPlayerWidth(computedWidth);
      setVideoPlayerHeight(videoElement?.clientHeight);
      if (videoSegmentation?.segmentationRunning) {
        context!.clearRect(0, 0, videoPlayerWidth, videoPlayerHeight);
        return;
      }

      if (videoSegmentation?.vaData.segmentationDetails && filteredSegmentations) {
        if (!videoPlayerService.isVideoPlaying) {
          renderClosestPredictionBasedOnVideoTime(context, videoPlayerService.videoProgress);
          return;
        }
        renderClosestPredictionBasedOnVideoTime(context, videoElement.currentTime);

        requestId.current = requestAnimationFrame(renderFramePredictions);
      }
    };

    renderFramePredictions();

    return () => {
      cancelAnimationFrame(requestId.current!);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFullscreen,
    videoSegmentation?.isLoading,
    videoSegmentation?.segmentationRunning,
    videoProgress,
    showArea,
    showContour,
    showBox,
    showLabels,
    filteredSegmentations,
    // Redraw when a damage class is toggled (Crack/Root/Connection…), even while
    // the video is paused — otherwise the per-class filter only takes effect on the
    // next played frame.
    detectedGroupCodes,
  ]);

  return (
    <canvas
      // Anchor to the top-left of the relative player container so the masks land on
      // the video. Without explicit top/left the canvas takes its static position
      // (after the video element) and draws below it.
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
      width={videoPlayerWidth}
      height={videoPlayerHeight}
      ref={canvasRef}
    />
  );
});
